// libs
import React, { memo, useEffect, useRef } from 'react';

// styles
import { Button, ButtonV2, Container, StockInfoV2 } from './styles';

// components
import StockInfo from './StockInfo';

// hooks
import { useRemoteConfig } from 'shared/store/remoteConfigs/hooks';

// defs
interface ISizeButtonProps {
  onClick: VoidFunction;
  isSelected?: boolean;
  isOutOfStock?: boolean;
  size: string;
  containerRef: React.RefObject<HTMLDivElement>;
  stockRemaining?: number;
  finalWidth?: number;
  isSizeButtonV2?: boolean;
  disabled?: boolean;
}

const SCROLL_PADDING = 40; // left and right gutters

function SizeButton({
  onClick,
  size,
  containerRef,
  isOutOfStock = false,
  isSelected = false,
  stockRemaining,
  finalWidth = 0,
  isSizeButtonV2 = false,
  disabled = false,
}: ISizeButtonProps) {
  const elRef = useRef<HTMLButtonElement>(null);
  const isEligible = useRemoteConfig('sizeInventoryNudge');
  /**
   * Selected Size option to be visible to user
   * as size container is horizontally scrollable
   */
  useEffect(() => {
    if (isSelected === false || elRef.current === null || containerRef.current === null) {
      return;
    }

    const { left: elLeft, top: elTop, height: elHeight } = elRef.current.getBoundingClientRect();

    const {
      width: contWidth,
      left: contLeft,
      bottom,
      top,
    } = containerRef.current.getBoundingClientRect();

    //for vertical scrolling
    const isVisible = elTop + elHeight < bottom;

    if (!isVisible) {
      containerRef.current.scrollBy(0, elTop - top);
      return;
    }

    // if element already visible dont do anything
    if (elLeft < contWidth) {
      return;
    }

    containerRef.current.scrollBy(elLeft - contLeft + SCROLL_PADDING, 0);
  }, [containerRef, isSelected]);

  const finalStockQty = isEligible ? (isSelected ? stockRemaining : false) : stockRemaining;

  const getAriaLabel = () => {
    if (disabled) return `Size ${size}, disabled`;
    if (isOutOfStock) return `Size ${size}, out of stock`;
    if (isSelected) return `Size ${size}, selected`;
    if (finalStockQty) return `Size ${size}, ${finalStockQty} remaining`;
    return `Select size ${size}`;
  };

  return (
    <Container ref={elRef} className="size-btn">
      {isSizeButtonV2 ? (
        <>
          <ButtonV2
            selected={isSelected}
            onClick={isSelected ? () => {} : onClick}
            data-test="size-btn"
            data-at="size-btn"
            label={size}
            size="small"
            type="button"
            width={finalWidth}
            disabled={disabled}
            aria-label={getAriaLabel()}
            aria-pressed={isSelected}
          >
            {size}
          </ButtonV2>
          <StockInfoV2
            isOutOfStock={isOutOfStock && !disabled}
            stockRemaining={disabled ? false : finalStockQty}
            aria-hidden="true"
          />
        </>
      ) : (
        <>
          <Button
            selected={isSelected}
            onClick={onClick}
            data-test="size-btn"
            data-at="size-btn"
            label={size}
            size="small"
            type="button"
            disabled={disabled}
            aria-label={getAriaLabel()}
            aria-pressed={isSelected}
          >
            {size}
          </Button>
          <StockInfo
            isOutOfStock={isOutOfStock && !disabled}
            stockRemaining={disabled ? false : finalStockQty}
            aria-hidden="true"
          />
        </>
      )}
    </Container>
  );
}

export default memo(SizeButton);
